import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import InviteField, { useAdmission } from 'components/admission/InviteField';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  TextField,
  Typography,
  Link
} from '@mui/material';
import * as yup from 'yup';
// Facebook registration is temporarily unavailable while its app integration is repaired.
// import FacebookIcon from './assets/facebook.png';
import GoogleIcon from './assets/google.png';
import AppleIcon from './assets/apple.png';
import { googleLoginUrl, appleLoginUrl } from 'utils/auth';
import api from 'utils/api';
import * as uiActions from '../../../../modules/UI/actions';
import { AuthPopupEnum } from 'utils/constants';
import HugLogoSrc from 'components/Logo/LogoFull.png';
import { Link as RouterLink } from 'react-router-dom';

const schema = yup.object().shape({
  email: yup.string().email().required('Email is required'),
  password: yup.string().required('Password is required'),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match'),
  terms: yup.boolean().oneOf([true], 'Please review and accept the terms and conditions'),
  businessAccount: yup.boolean(),
});

const Register = ({ onSuccess, onClose, onOpenLogin }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [inviteCode, setInviteCode] = useState('');
  const [inviteToken, setInviteToken] = useState(() => new URLSearchParams(window.location.search).get('invite') || '');
  const [resolvedInvite, setResolvedInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [validatingInvite, setValidatingInvite] = useState(false);
  const validationVersion = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isBusiness, setIsBusiness] = useState(null);
  const accountType = isBusiness ? 'BUSINESS' : 'PERSONAL';
  const admission = useAdmission(accountType);
  const [isEmailVerified, setIsEmailVerified] = useState(false); // Track email verification status
  const [email, setEmail] = useState('');
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const returnUrl = encodeURIComponent(`?return=${true ? '/' : '/business'}`);
  const invitationRequired = admission.status?.mode === 'INVITE';
  const signupBlocked = admission.blocked || admission.status?.available === false;
  const invitationReady = (!invitationRequired && !inviteToken && !inviteCode) || Boolean(resolvedInvite);
  const invitePayload = inviteToken ? { inviteToken } : inviteCode ? { inviteCode: inviteCode.trim() } : {};

  useEffect(() => {
    if (!inviteToken) return;
    let active = true;
    setValidatingInvite(true);
    api.post('/auth/invitations/resolve', { inviteToken })
      .then(result => { if (active) { setResolvedInvite(result); setInviteError(''); } })
      .catch(error => { if (active) { setInviteToken(''); setInviteError(error.message || 'Invitation link is invalid. Enter an invite code.'); } })
      .finally(() => { if (active) setValidatingInvite(false); });
    return () => { active = false; };
  }, [inviteToken]);

  const changeInviteCode = value => {
    validationVersion.current += 1;
    setInviteCode(value);
    setResolvedInvite(null);
    setInviteError('');
  };

  const validateInviteCode = async () => {
    const version = ++validationVersion.current;
    setValidatingInvite(true);
    try {
      const result = await api.post('/auth/invitations/resolve', { inviteCode: inviteCode.trim() });
      if (version === validationVersion.current) { setResolvedInvite(result); setInviteError(''); }
    } catch (error) {
      if (version === validationVersion.current) setInviteError(error.message || 'This invitation is invalid.');
    } finally {
      if (version === validationVersion.current) setValidatingInvite(false);
    }
  };

  const clearInvitation = () => {
    validationVersion.current += 1;
    setInviteToken('');
    setInviteCode('');
    setResolvedInvite(null);
    setInviteError('');
    if (window.location.pathname === '/signup') window.history.replaceState(null, '', '/signup');
  };

  const handleLoginClick = useCallback(() => {
    dispatch(uiActions.hideLogin());
    setTimeout(() => {
      dispatch(uiActions.showLogin(AuthPopupEnum.login));
    }, 100);
  }, [dispatch]);

  useEffect(() => {
    // After registration, you can check if the email is verified
    if (isRegistered) {
      api.post('/auth/email-verified', { email })
        .then(response => {
          console.log("RESPONSE--------------- ",response);
          setIsEmailVerified(response.emailVerified); // Set verification status based on API response
          console.log('Email verification status:', response.emailVerified);
        })
        .catch(() => {
          enqueueSnackbar('Error checking email verification', { variant: 'error' });
        });
    }
  }, [isRegistered, email, enqueueSnackbar]);

  useEffect(() => {
    const checkVerification = () => {
      if (isRegistered) {
        api.post('/auth/email-verified', { email })
          .then(response => {
            console.log("RESPONSE--------------- ",response);
          setIsEmailVerified(response.emailVerified); // Set verification status based on API response
          console.log('Email verification status:', response.emailVerified);
          })
          .catch(() => {
            enqueueSnackbar('Error checking email verification', { variant: 'error' });
          });
      }
    };
  
    window.addEventListener('focus', checkVerification);
  
    return () => {
      window.removeEventListener('focus', checkVerification);
    };
  }, [isRegistered, email, enqueueSnackbar]);

  useEffect(() => {
    if (isEmailVerified) {
      sessionStorage.setItem("emailVerified", "true");
      const timer = setTimeout(handleLoginClick, 3000);

      return () => clearTimeout(timer);
    }
  }, [isEmailVerified, handleLoginClick]);

  const handleFormSubmit = (data) => {
    if (isBusiness === null) {
      enqueueSnackbar("Please choose either a business or personal account to continue with your account registration", { variant: 'error' });
      return;
    }

    if (submitting || signupBlocked || !invitationReady || validatingInvite) return;
    setSubmitting(true);
    const payload = {
      accountType,
      ...invitePayload,
      email: data.email,
      password: data.password,
    };
    //sessionStorage.setItem("email", data.email);
    setEmail(data.email); // Store email in state for verification check
    api
      .post('/auth/signup', payload)
      .then(() => {
        setIsRegistered(true);
        if (onSuccess) {
          onSuccess();  // Call the success callback to redirect to the success page
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
        admission.reload();
      }).finally(() => setSubmitting(false));
  };

  const handleSocialLogin = async (provider) => {
    if (isBusiness === null) {
      enqueueSnackbar("Please choose account type first", { variant: 'error' });
      return;
    }

    const baseUrl = provider === 'apple' ? appleLoginUrl : googleLoginUrl;
    if (submitting || signupBlocked || !invitationReady || validatingInvite) return;
    setSubmitting(true);
    try {
      const { ticket } = await api.post('/auth/admission/oauth', { accountType, ...invitePayload });
      window.location.href = `${baseUrl}${returnUrl}&admission_ticket=${encodeURIComponent(ticket)}`;
    } catch (err) {
      enqueueSnackbar(err.message || 'Registration could not be started.', { variant: 'error' });
      admission.reload();
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto', py: 3 }}>
      {/* Logo with added spacing */}
      <img
        src={HugLogoSrc}
        height={60}
        alt="HiddenUnderGround logo"
        style={{ marginBottom: '20px' }} // Added spacing below the logo
      />

      {isRegistered ? (
        isEmailVerified ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: '500', mb: 2 }}>
              Your Email is Verified!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              You can now proceed to login.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ height: 50, mb: 2 }}
              onClick={handleLoginClick}
            >
              Login Now
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: '500', mb: 2 }}>
              Verify Your Email Address
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              A verification link has been sent to your email. Please check your inbox and follow the instructions to verify your account.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Didn’t receive the email? Check your spam folder or{' '}
              <Typography component="span" color="primary" sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                Resend verification email
              </Typography>.
            </Typography>
          </>
        )
      ) : (
        <>
          <Typography variant="h6" sx={{ textAlign: 'left', fontWeight: 500, mb: 1 }}>
            I want to register for:
          </Typography>

          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            {/* Personal and Business Account Selection in One Line */}
            <Box my={2} display="flex" justifyContent="space-between">
              <FormControlLabel
                control={<Radio size="small" checked={isBusiness === false} onChange={() => setIsBusiness(false)} />}
                label="Personal account"
              />
              <FormControlLabel
                control={<Radio size="small" checked={isBusiness === true} onChange={() => setIsBusiness(true)} />}
                label="Business user"
              />
            </Box>

            {new URLSearchParams(window.location.search).get('reason') === 'invite_required' &&
              <Typography color="text.secondary" sx={{ mb: 1 }}>A new Google or Apple account needs an invitation. Enter a code or use an invitation link, then continue.</Typography>}
            {isBusiness !== null && <InviteField admission={admission} value={inviteCode} onChange={changeInviteCode}
              onValidate={validateInviteCode} onClear={clearInvitation} validating={validatingInvite}
              resolved={resolvedInvite} error={inviteError} />}

            {/* Email Field */}
            <FormControl fullWidth error={!!errors.email} sx={{ mb: 2 }}>
              <TextField label="Email" {...register('email')} variant="outlined" fullWidth />
              {!!errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
            </FormControl>

            {/* Password Field */}
            <FormControl fullWidth error={!!errors.password} sx={{ mb: 2 }}>
              <TextField label="Password" type="password" {...register('password')} variant="outlined" fullWidth />
              {!!errors.password && <FormHelperText>{errors.password.message}</FormHelperText>}
            </FormControl>

            {/* Confirm Password Field */}
            <FormControl fullWidth error={!!errors.passwordConfirmation} sx={{ mb: 3 }}>
              <TextField label="Confirm password" type="password" {...register('passwordConfirmation')} variant="outlined" fullWidth />
              {!!errors.passwordConfirmation && <FormHelperText>{errors.passwordConfirmation.message}</FormHelperText>}
            </FormControl>

            {/* Terms & Conditions */}
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
              By clicking on <strong>"Create account"</strong>, I agree to the
              <Link
                component={RouterLink} // Use RouterLink for navigation
                to="/terms" // Navigate to /terms
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', ml: 0.5 }}
                onClick={onClose}
              >
                Terms of Service
              </Link>
              , and
              <Link
                component={RouterLink} // Use RouterLink for navigation
                to="/privacy" // Navigate to /privacy
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', ml: 0.5 }}
                onClick={onClose}
              >
                Privacy Policy
              </Link>
              .
            </Typography>

            {/* Create Account Button */}
            <Button disabled={submitting || signupBlocked || isBusiness === null || !invitationReady || validatingInvite} type="submit" variant="contained" color="primary" fullWidth sx={{ height: 50, mb: 2 }}>
              Create an account
            </Button>
          </form>

          {/* Already a user? Log In */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Already a user?{' '}
            <Typography
              component="span"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleLoginClick}
            >
              Log in
            </Typography>
          </Typography>
        </>
      )}

      {/* Social Media Sign Up Buttons - Only show when not registered */}
      {!isRegistered && (
        <Box>
          <Button 
            variant="outlined" 
            fullWidth 
            sx={{ height: 50, mb: 1 }} 
            disabled={submitting || signupBlocked || isBusiness === null || !invitationReady || validatingInvite}
            onClick={() => handleSocialLogin('google')}
          >
            <Box component="img" src={GoogleIcon} alt="Google" sx={{ height: 32, mr: 1 }} /> Register with Google
          </Button>
          <Button 
            variant="outlined" 
            fullWidth 
            sx={{ height: 50, mb: 1 }} 
            disabled={submitting || signupBlocked || isBusiness === null || !invitationReady || validatingInvite}
            onClick={() => handleSocialLogin('apple')}
          >
            <Box component="img" src={AppleIcon} alt="Apple" sx={{ height: 22, mr: 1 }} /> Register with Apple
          </Button>
          {/* Facebook registration can be restored after the Facebook app is repaired.
          <Button 
            variant="outlined" 
            fullWidth 
            sx={{ height: 50 }} 
            disabled={submitting || admission.blocked || isBusiness === null}
            onClick={() => handleSocialLogin('facebook')}
          >
            <Box component="img" src={FacebookIcon} alt="Facebook" sx={{ height: 22, mr: 1 }} /> Register with Facebook
          </Button>
          */}
        </Box>
      )}
    </Box>
  );
};

export default Register;
